import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Box,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { authApi } from "@/services/authApi";
import { invitationService } from "@/services/InvitationService";
import { setToken } from "@/utils/auth";
import type { RegistrationForm } from "@/types/Registration";
import type { ApiError, LoginResponse } from "@/types/auth.type";
import type { User } from "@/types/Sidebar.type";
import TermsAndConditionsModal from "./TermsAndConditionsModal";
import { CLINICAL_FONT_FAMILY } from "@/components/shared/constants";
import { getPasswordComplexityErrors } from "@/utils/passwordValidation";
interface RegistrationPageProps {
  onLogin: (userData: User) => void;
}
export default function RegistrationPage({ onLogin }: RegistrationPageProps) {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInvite, setIsLoadingInvite] = useState(true);
  const [error, setError] = useState<string>("");
  const [inviteError, setInviteError] = useState<string>("");
  const [requiresRoleAssignment, setRequiresRoleAssignment] = useState(false);
  const [isTenantAdminInvite, setIsTenantAdminInvite] = useState(false);
  const [hasValidLogoUrl, setHasValidLogoUrl] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUploadError, setLogoUploadError] = useState("");
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [invitationEmail, setInvitationEmail] = useState<string>("");
  const [invitationName, setInvitationName] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const inviteToken = searchParams.get("token");
  const navigate = useNavigate();
  // Fetch invitation details when token is available
  useEffect(() => {
    const fetchInvitationDetails = async () => {
      if (!inviteToken) {
        setError(
          "No invitation token provided. Please use the link from your invitation email.",
        );
        setIsLoadingInvite(false);
        return;
      }
      setIsLoadingInvite(true);
      try {
        const inviteDetails =
          await invitationService.getInviteById(inviteToken);
        if (!inviteDetails) {
          setInviteError(
            "This invitation link is invalid or has already been used. Please contact your administrator to send a new invitation.",
          );
          setIsLoadingInvite(false);
          return;
        }
        const inviteMetadata = inviteDetails.inviteMetadata as
          | {
              tenantId?: string;
              roles?: unknown[];
              tenantDetails?: { logoUrl?: string };
              inviteeName?: string;
            }
          | undefined;
        const normalUserInviteHasNoRoles =
          !!inviteMetadata?.tenantId &&
          (!inviteMetadata?.roles || inviteMetadata.roles.length === 0);
        setRequiresRoleAssignment(normalUserInviteHasNoRoles);
        const tenantAdminInvite = !!inviteMetadata?.tenantDetails;
        setIsTenantAdminInvite(tenantAdminInvite);
        // Track whether a valid logo URL was already provided; if not, show the upload field
        setHasValidLogoUrl(
          tenantAdminInvite && !!inviteMetadata?.tenantDetails?.logoUrl,
        );
        // Extract email from invitation
        const email = inviteDetails.email || "";
        setInvitationEmail(email);
        // Use the invitee name from the invite metadata if available,
        // otherwise derive from email as a fallback
        const inviteeName = inviteDetails.inviteMetadata?.inviteeName;
        let displayName = inviteeName?.trim() ?? "";
        if (!displayName) {
          const namePart = email.split("@")[0] ?? "";
          displayName = namePart
            .split(/[._-]/)
            .map(
              (part) =>
                part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
            )
            .join(" ");
        }
        setInvitationName(displayName);
        // Set default form values
        reset({
          email,
          fullName: displayName,
          password: "",
          confirmPassword: "",
        });
        setError("");
      } catch {
        setInviteError(
          "This invitation link is invalid or has expired. Please contact your administrator to request a new invitation.",
        );
        setRequiresRoleAssignment(false);
      } finally {
        setIsLoadingInvite(false);
      }
    };
    void fetchInvitationDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load invite once per token
  }, [inviteToken]);
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<RegistrationForm>();
  const onSubmit = async (data: RegistrationForm) => {
    if (!inviteToken) {
      setError(
        "No invitation token found. Please use the link from your invitation email.",
      );
      return;
    }
    // Validate T&C acceptance
    if (!termsAccepted || !privacyAccepted) {
      setError(
        "You must accept both Terms and Conditions and Privacy Policy to continue.",
      );
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const response = await authApi.register({
        inviteToken,
        email: invitationEmail, // Use email from invitation
        fullName: invitationName, // Use name from invitation
        password: data.password,
        acceptedTnc: termsAccepted,
        acceptedPrivacyPolicy: privacyAccepted,
      });
      // After successful registration, automatically log in the user.
      // If this invite did not include roles, backend login will be blocked
      // (non-tenant-admin users) until tenant admin assigns roles.
      if (requiresRoleAssignment) {
        navigate("/login?rolesPending=1");
        return;
      }
      try {
        const loginResponse = (await authApi.login({
          email: invitationEmail,
          password: data.password,
        })) as LoginResponse;
        if (loginResponse.accessToken && loginResponse.user) {
          setToken(loginResponse.accessToken);

          // If this is a tenant admin invite and a logo file was selected,
          // upload it now that we have a valid JWT and a tenantId.
          if (isTenantAdminInvite && logoFile) {
            const tenantId = (response as { tenantId?: string })?.tenantId;
            if (tenantId) {
              try {
                await authApi.uploadTenantLogo(tenantId, logoFile);
              } catch {
                // Logo upload failure is non-fatal — the user is still registered
                setLogoUploadError(
                  "Logo upload failed. You can update the logo later from your profile settings.",
                );
              }
            }
          }

          onLogin(loginResponse.user);
          // Navigation will be handled by the App component after login
        } else {
          setError(
            "Registration successful but auto-login failed. Please login manually.",
          );
        }
      } catch (loginError) {
        // Handle server error from login
        if (
          loginError &&
          typeof loginError === "object" &&
          "message" in loginError
        ) {
          const apiError = loginError as ApiError;
          setError(apiError.message);
        } else {
          setError(
            "Registration successful! Please login with your new credentials.",
          );
        }
      }
    } catch (error: unknown) {
      // Handle server errors specifically
      if (error && typeof error === "object" && "message" in error) {
        const apiError = error as ApiError;
        setError(apiError.message);
      } else if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("Registration failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };
  // If no token, show error message with login button
  if (!inviteToken) {
    return (
      <Container maxWidth="xs" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" mb={3}>
            Invalid Registration Link
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            No invitation token provided. Please use the link from your
            invitation email.
          </Alert>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              void navigate("/login");
            }}
          >
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }
  // Show loading state while fetching invitation details
  if (isLoadingInvite) {
    return (
      <Container maxWidth="xs" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body1" color="textSecondary">
            Loading your invitation details...
          </Typography>
        </Paper>
      </Container>
    );
  }
  // Show a clear error screen when the invite token is invalid or expired
  if (inviteError) {
    return (
      <Container maxWidth="xs" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4 }}>
          <Typography
            variant="h5"
            mb={2}
            sx={{ fontFamily: CLINICAL_FONT_FAMILY, fontWeight: 600 }}
          >
            Invitation Link Invalid
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {inviteError}
          </Alert>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mb: 3, fontFamily: CLINICAL_FONT_FAMILY }}
          >
            If you believe this is a mistake, please reach out to your study
            administrator and ask them to resend the invitation.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              void navigate("/login");
            }}
            sx={{ fontFamily: CLINICAL_FONT_FAMILY }}
          >
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }
  return (
    <Container maxWidth="xs" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" mb={3}>
          Complete Registration
        </Typography>
        <form
          onSubmit={(event) => {
            void handleSubmit(onSubmit)(event);
          }}
        >
          {/* Email Field - Read Only */}
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Email is required",
            }}
            render={() => (
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Email"
                  fullWidth
                  margin="normal"
                  value={invitationEmail}
                  disabled={true}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                  sx={{
                    backgroundColor: "#F9FAFB",
                    "& .MuiOutlinedInput-root": {
                      cursor: "not-allowed",
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6B7280",
                    display: "block",
                    mt: 0.5,
                  }}
                >
                  This email was from your invitation
                </Typography>
              </Box>
            )}
          />

          {/* Full Name Field - Read Only */}
          <Controller
            name="fullName"
            control={control}
            rules={{
              required: "Full name is required",
            }}
            render={() => (
              <Box sx={{ mb: 2 }}>
                <TextField
                  label="Full Name"
                  fullWidth
                  margin="normal"
                  value={invitationName}
                  disabled={true}
                  slotProps={{
                    input: {
                      readOnly: true,
                    },
                  }}
                  sx={{
                    backgroundColor: "#F9FAFB",
                    "& .MuiOutlinedInput-root": {
                      cursor: "not-allowed",
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6B7280",
                    display: "block",
                    mt: 0.5,
                  }}
                >
                  Auto-generated from your email. Contact admin to change it.
                </Typography>
              </Box>
            )}
          />
          <Controller
            name="password"
            control={control}
            rules={{
              required: "Password is required",
              validate: (value) => {
                const errors = getPasswordComplexityErrors(value);
                return errors[0] ?? true;
              },
            }}
            render={({ field }) => (
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                fullWidth
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={isLoading}
                {...field}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            void setShowPassword(!showPassword);
                          }}
                          edge="end"
                          disabled={isLoading}
                        >
                          {showPassword ? (
                            <EyeOff size={20} />
                          ) : (
                            <Eye size={20} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
          <Controller
            name="confirmPassword"
            control={control}
            rules={{
              required: "Please confirm your password",
              validate: (value) => {
                const password = watch("password");
                if (value !== password) {
                  return "Passwords do not match";
                }
                return true;
              },
            }}
            render={({ field }) => (
              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                fullWidth
                margin="normal"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message}
                disabled={isLoading}
                {...field}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                          disabled={isLoading}
                        >
                          {showConfirmPassword ? (
                            <EyeOff size={20} />
                          ) : (
                            <Eye size={20} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          {logoUploadError && (
            <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
              {logoUploadError}
            </Alert>
          )}

          {/* Logo upload — only for tenant admin invites when no valid logo URL was
              provided at invite time. This gives the admin a chance to supply their
              organisation logo during the initial sign-up flow. */}
          {isTenantAdminInvite && !hasValidLogoUrl && (
            <Box
              sx={{
                mt: 2,
                mb: 2,
                p: 2,
                border: "1px dashed #9CA3AF",
                borderRadius: 1,
                backgroundColor: "#F9FAFB",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  fontWeight: 600,
                  mb: 0.5,
                  color: "#374151",
                }}
              >
                Organisation Logo{" "}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "#6B7280", fontWeight: 400 }}
                >
                  (optional)
                </Typography>
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#6B7280", display: "block", mb: 1 }}
              >
                Upload a logo image (JPEG, PNG, GIF, WebP or SVG, max 5 MB)
              </Typography>
              <Button
                component="label"
                variant="outlined"
                size="small"
                disabled={isLoading}
                sx={{ fontFamily: CLINICAL_FONT_FAMILY }}
              >
                {logoFile ? "Change File" : "Choose File"}
                <input
                  hidden
                  accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null;
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        setLogoUploadError(
                          "Selected file exceeds the 5 MB size limit. Please choose a smaller image.",
                        );
                        e.target.value = "";
                        return;
                      }
                      setLogoUploadError("");
                    }
                    setLogoFile(file);
                  }}
                />
              </Button>
              {logoFile && (
                <Typography variant="caption" sx={{ ml: 1, color: "#374151" }}>
                  {logoFile.name}
                </Typography>
              )}
            </Box>
          )}

          {/* Terms and Conditions Checkboxes */}
          <Box
            sx={{
              backgroundColor:
                !termsAccepted || !privacyAccepted ? "#FEF2F2" : "#F0F9FF",
              p: 2,
              borderRadius: 1,
              my: 2,
              border:
                !termsAccepted || !privacyAccepted
                  ? "1px solid #FECACA"
                  : "1px solid #BFE7FF",
              transition: "all 0.3s ease",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  disabled={isLoading}
                  sx={{
                    color: "#D1D5DB",
                    "&.Mui-checked": {
                      color: "#0B5FFF",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: CLINICAL_FONT_FAMILY,
                      color: "#6B7280",
                    }}
                  >
                    I agree to the
                  </Typography>
                  <Typography
                    component="button"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setTermsModalOpen(true);
                    }}
                    sx={{
                      fontFamily: CLINICAL_FONT_FAMILY,
                      color: "#0B5FFF",
                      textDecoration: "underline",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 0,
                      "&:hover": {
                        opacity: 0.8,
                      },
                    }}
                  >
                    Terms and Conditions
                  </Typography>
                </Box>
              }
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  disabled={isLoading}
                  sx={{
                    color: "#D1D5DB",
                    "&.Mui-checked": {
                      color: "#0B5FFF",
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: CLINICAL_FONT_FAMILY,
                      color: "#6B7280",
                    }}
                  >
                    I agree to the
                  </Typography>
                  <Typography
                    component="a"
                    href="#"
                    sx={{
                      fontFamily: CLINICAL_FONT_FAMILY,
                      color: "#0B5FFF",
                      textDecoration: "underline",
                      "&:hover": {
                        opacity: 0.8,
                      },
                    }}
                  >
                    Privacy Policy
                  </Typography>
                </Box>
              }
            />

            {/* Required Indicator */}
            {(!termsAccepted || !privacyAccepted) && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "#DC2626",
                  fontFamily: CLINICAL_FONT_FAMILY,
                  fontWeight: 500,
                  mt: 1,
                  ml: 1,
                }}
              >
                * Both Terms and Conditions and Privacy Policy must be accepted
              </Typography>
            )}
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            disabled={isLoading || !termsAccepted || !privacyAccepted}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? "Activating..." : "Activate Account"}
          </Button>
        </form>

        {/* Terms and Conditions Modal */}
        <TermsAndConditionsModal
          open={termsModalOpen}
          onClose={() => setTermsModalOpen(false)}
          onAccept={() => {
            setTermsAccepted(true);
            setTermsModalOpen(false);
          }}
          onReject={() => {
            setTermsAccepted(false);
            setTermsModalOpen(false);
          }}
          loading={isLoading}
        />
      </Paper>
    </Container>
  );
}
